import React, { useState } from 'react';
import { Code, Copy, Trash2 } from 'lucide-react';

const RemoveHtmlTagsTool = () => {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [options, setOptions] = useState({
    preserveLineBreaks: true,
    preserveLinks: false,
    decodeEntities: true
  });
  const [copied, setCopied] = useState(false);
  const [stats, setStats] = useState({ before: 0, after: 0, tagsRemoved: 0 });

  const decodeHtmlEntities = (text) => {
    const entities = {
      '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"',
      '&#39;': "'", '&nbsp;': ' ', '&copy;': '\u00A9', '&reg;': '\u00AE',
      '&trade;': '\u2122', '&euro;': '\u20AC', '&pound;': '\u00A3', '&yen;': '\u00A5',
      '&cent;': '\u00A2', '&mdash;': '\u2014', '&ndash;': '\u2013', '&hellip;': '\u2026',
      '&lsquo;': '\u2018', '&rsquo;': '\u2019', '&ldquo;': '\u201C', '&rdquo;': '\u201D'
    };
    let result = text;
    Object.entries(entities).forEach(([entity, char]) => {
      result = result.replace(new RegExp(entity, 'gi'), char);
    });
    // Handle numeric entities
    result = result.replace(/&#(\d+);/g, (_, num) => String.fromCharCode(num));
    result = result.replace(/&#x([0-9a-f]+);/gi, (_, hex) => String.fromCharCode(parseInt(hex, 16)));
    return result;
  };

  const removeHtml = (text) => {
    if (!text.trim()) {
      setOutput('');
      setStats({ before: 0, after: 0, tagsRemoved: 0 });
      return;
    }

    let result = text;
    const tagCount = (text.match(/<[^>]+>/g) || []).length;

    // Preserve line breaks if option is enabled
    if (options.preserveLineBreaks) {
      result = result.replace(/<br\s*\/?>/gi, '\n');
      result = result.replace(/<\/p>/gi, '\n\n');
      result = result.replace(/<\/div>/gi, '\n');
      result = result.replace(/<\/li>/gi, '\n');
      result = result.replace(/<\/tr>/gi, '\n');
    }

    // Extract links if option is enabled
    if (options.preserveLinks) {
      result = result.replace(/<a[^>]*href=["']([^"']*)["'][^>]*>([^<]*)<\/a>/gi, '$2 ($1)');
    }

    // Remove all remaining HTML tags
    result = result.replace(/<[^>]+>/g, '');

    // Decode HTML entities if option is enabled
    if (options.decodeEntities) {
      result = decodeHtmlEntities(result);
    }

    // Clean up extra whitespace
    result = result.replace(/[ \t]+/g, ' ');
    result = result.replace(/\n\s*\n\s*\n/g, '\n\n');
    result = result.trim();

    setOutput(result);
    setStats({
      before: text.length,
      after: result.length,
      tagsRemoved: tagCount
    });
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
    removeHtml(e.target.value);
  };

  const handleOptionChange = (option) => {
    const newOptions = { ...options, [option]: !options[option] };
    setOptions(newOptions);
    // Re-process with new options
    if (input) {
      setTimeout(() => removeHtml(input), 0);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(output);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const clearAll = () => {
    setInput('');
    setOutput('');
    setStats({ before: 0, after: 0, tagsRemoved: 0 });
  };

  const loadSample = () => {
    const sample = `<html>
<head><title>Sample Page</title></head>
<body>
  <h1>Welcome to Our Website</h1>
  <p>This is a <strong>sample</strong> paragraph with <em>formatted</em> text.</p>
  <p>Visit our <a href="https://example.com">website</a> for more info.</p>
  <ul>
    <li>Item 1</li>
    <li>Item 2</li>
    <li>Item 3</li>
  </ul>
  <p>Contact us at info@example.com &amp; we'll respond within 24 hours.</p>
</body>
</html>`;
    setInput(sample);
    removeHtml(sample);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-4 p-4 bg-gray-800/50 rounded-lg">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={options.preserveLineBreaks}
            onChange={() => handleOptionChange('preserveLineBreaks')}
            className="w-4 h-4 rounded bg-gray-700 border-gray-600 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-300">Preserve line breaks</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={options.preserveLinks}
            onChange={() => handleOptionChange('preserveLinks')}
            className="w-4 h-4 rounded bg-gray-700 border-gray-600 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-300">Extract links</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={options.decodeEntities}
            onChange={() => handleOptionChange('decodeEntities')}
            className="w-4 h-4 rounded bg-gray-700 border-gray-600 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-300">Decode HTML entities</span>
        </label>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="text-sm font-medium text-gray-300">HTML Input</label>
            <button
              onClick={loadSample}
              className="text-xs text-blue-400 hover:text-blue-300"
            >
              Load Sample
            </button>
          </div>
          <textarea
            value={input}
            onChange={handleInputChange}
            placeholder="Paste HTML code here..."
            className="w-full h-64 bg-gray-800 border border-gray-700 rounded-lg p-4 text-white font-mono text-sm placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Plain Text Output
          </label>
          <textarea
            value={output}
            readOnly
            placeholder="Clean text will appear here..."
            className="w-full h-64 bg-gray-800 border border-gray-700 rounded-lg p-4 text-white placeholder-gray-500"
          />
        </div>
      </div>

      {stats.tagsRemoved > 0 && (
        <div className="grid grid-cols-3 gap-4">
          <div className="p-3 bg-gray-800 rounded-lg text-center">
            <div className="text-xl font-bold text-red-400">{stats.tagsRemoved}</div>
            <div className="text-xs text-gray-500">Tags Removed</div>
          </div>
          <div className="p-3 bg-gray-800 rounded-lg text-center">
            <div className="text-xl font-bold text-blue-400">{stats.before}</div>
            <div className="text-xs text-gray-500">Original Chars</div>
          </div>
          <div className="p-3 bg-gray-800 rounded-lg text-center">
            <div className="text-xl font-bold text-green-400">{stats.after}</div>
            <div className="text-xs text-gray-500">Clean Chars</div>
          </div>
        </div>
      )}

      <div className="flex gap-3">
        <button
          onClick={copyToClipboard}
          disabled={!output}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
        >
          <Copy size={16} />
          {copied ? 'Copied!' : 'Copy Text'}
        </button>
        <button
          onClick={clearAll}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
        >
          <Trash2 size={16} />
          Clear
        </button>
      </div>
    </div>
  );
};

export default RemoveHtmlTagsTool;
